import React, { useState, useEffect } from 'react';
import axios from 'axios'; // Import axios
import { Button } from '../common'; // Assuming Button is used or needed

const ViewEmployeeModal = ({ isOpen, onClose, employeeId, onEdit }) => { // Changed employee prop to employeeId
  const [employeeData, setEmployeeData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const API_BASE_URL = '/api';

  useEffect(() => {
    if (isOpen && employeeId) {
      setLoading(true);
      setError('');
      setEmployeeData(null); // Clear previous data

      const fetchEmployeeDetails = async () => {
        try {
          const token = localStorage.getItem('token');
          if (!token) {
            throw new Error('Authentication token not found.');
          }
          const config = { headers: { 'x-auth-token': token } };
          console.log(`Fetching employee details for ID: ${employeeId}`); // Log fetch start
          // Use the newer /api/employees/:id endpoint directly instead of the deprecated route
          const res = await axios.get(`${API_BASE_URL}/employees/${employeeId}`, config);
          console.log('API Response Data:', res.data); // Log the raw response data
          if (res.data) {
            console.log('Setting employeeData state with fetched data.'); // Log before setting state
            setEmployeeData(res.data); // Store fetched data
          } else {
             console.warn('API returned success status but no data.'); // Log if data is unexpectedly empty
             setError('Received empty data for employee.');
          }
        } catch (err) {
          console.error('Error fetching employee details:', err); // Log the full error
          setError(`Failed to load employee details: ${err.message}`); // Include error message
        } finally {
          setLoading(false);
        }
      };

      fetchEmployeeDetails();
    } else if (!isOpen) {
      // Reset state when modal closes
      setEmployeeData(null);
      setError('');
      setLoading(false);
    }
  }, [isOpen, employeeId]); // Rerun if modal opens or employeeId changes

  // Format date to readable format
  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
        // Attempt to create a date and format it
        return new Date(dateString).toLocaleDateString(undefined, { // Use locale default, add options if needed
            year: 'numeric', month: 'short', day: 'numeric'
        });
    } catch (e) {
        console.error("Error formatting date:", dateString, e);
        return dateString; // Return original string if formatting fails
    }
  };

  // Format date and time
   const formatDateTime = (dateString) => {
    if (!dateString) return '—';
     try {
        return new Date(dateString).toLocaleString(undefined, { // Use locale default
            year: 'numeric', month: 'short', day: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
     } catch(e) {
        console.error("Error formatting datetime:", dateString, e);
        return dateString;
     }
  };

  // Format hourly rate with currency
  const formatHourlyRate = (rate, country) => {
    if (rate === null || rate === undefined || rate === '') return '—'; // Check for null/undefined/empty string

    const currency = country === 'Norway' ? 'NOK' :
                     country === 'Sweden' ? 'SEK' :
                     country === 'Germany' ? 'EUR' : // Assuming Germany uses EUR
                     ''; // Default or fallback currency symbol if needed

    // Format the number itself
    const formattedRate = typeof rate === 'number' ? rate.toFixed(2) : rate;

    return `${formattedRate} ${currency}`;
  };

  // Render null if modal is not open
  if (!isOpen) return null;

  // Function to render role badge
  const renderRoleBadge = (roleName) => {
    const role = roleName ? roleName.toLowerCase() : '';
    let bgColor = 'bg-gray-100';
    let textColor = 'text-gray-800';

    switch (role) {
      case 'admin':
        bgColor = 'bg-purple-100'; textColor = 'text-purple-800'; break;
      case 'tutor':
        bgColor = 'bg-green-100'; textColor = 'text-green-800'; break;
      case 'manager': // Assuming 'manager' role exists and needs styling
        bgColor = 'bg-blue-100'; textColor = 'text-blue-800'; break;
      case 'sales':
        bgColor = 'bg-yellow-100'; textColor = 'text-yellow-800'; break;
      case 'superuser':
        bgColor = 'bg-red-100'; textColor = 'text-red-800'; break;
      default:
        break; // Keep default gray for unknown roles
    }
    return (
      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${bgColor} ${textColor}`}>
        {roleName || 'user'}
      </span>
    );
  };


  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4"
      onClick={onClose} // Close when clicking overlay
    >
      <div
        className="bg-white rounded-lg w-full max-w-lg max-h-[85vh] overflow-y-auto shadow-xl flex flex-col"
        onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside modal
      >
        {/* Header */}
        <div className="p-3 border-b flex justify-between items-center sticky top-0 bg-white z-10">
          <h3 className="text-lg font-semibold text-gray-800">Employee Details</h3>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        {/* Body */}
        <div className="p-4 flex-grow">
          {loading ? (
            <div className="flex justify-center items-center h-48">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : error ? (
            <div className="text-center text-red-600 bg-red-50 p-4 rounded border border-red-200">{error}</div>
          ) : employeeData ? (
            <>
              {/* Employee header */}
              <div className="flex items-center mb-4">
                <div className="h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center mr-4 flex-shrink-0">
                  <span className="text-blue-800 font-medium text-xl">
                    {employeeData.name ? employeeData.name.charAt(0).toUpperCase() : "?"}
                  </span>
                </div>
                <div>
                  <h2 className="text-xl font-semibold">{employeeData.name}</h2>
                  <p className="text-gray-500">{employeeData.email}</p>
                  <div className="mt-1">
                    {/* Use role_name from the API response */}
                    {renderRoleBadge(employeeData.role_name)}
                  </div>
                </div>
              </div>

              {/* Contact Info */}
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-gray-500 mb-2 border-b pb-1">CONTACT INFORMATION</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  <div><p className="text-gray-500">Phone</p><p className="font-medium">{employeeData.phone || '—'}</p></div>
                  <div><p className="text-gray-500">Email</p><p className="font-medium">{employeeData.email}</p></div>
                  <div><p className="text-gray-500">Country</p><p className="font-medium">{employeeData.country || '—'}</p></div>
                  <div><p className="text-gray-500">Address</p><p className="font-medium whitespace-pre-wrap">{employeeData.address || '—'}</p></div>
                </div>
              </div>

              {/* Employment Details */}
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-gray-500 mb-2 border-b pb-1">EMPLOYMENT DETAILS</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  <div><p className="text-gray-500">Hire Date</p><p className="font-medium">{formatDate(employeeData.hire_date)}</p></div>
                  <div><p className="text-gray-500">Status</p><p className="font-medium">{employeeData.status || 'Active'}</p></div>
                  <div><p className="text-gray-500">Hourly Rate</p><p className="font-medium">{formatHourlyRate(employeeData.hourly_rate, employeeData.country)}</p></div>
                  <div><p className="text-gray-500">Employee ID</p><p className="font-medium">{employeeData.id}</p></div>
                  <div><p className="text-gray-500">Last Active</p><p className="font-medium">{formatDateTime(employeeData.last_active)}</p></div>
                  <div><p className="text-gray-500">Bank Account</p><p className="font-medium">{employeeData.bank_account || '—'}</p></div>
                </div>
              </div>

              {/* Tutor Specific Info */}
              {/* Use role_name for the condition */}
              {employeeData.role_name === 'tutor' && (
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-gray-500 mb-2 border-b pb-1">TUTOR INFORMATION</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                    <div><p className="text-gray-500">University</p><p className="font-medium">{employeeData.university || '—'}</p></div>
                    <div><p className="text-gray-500">Study Program</p><p className="font-medium">{employeeData.study_program || '—'}</p></div>
                    {/* Terms Signed Status */}
                    <div className="col-span-2 mt-2 pt-2 border-t">
                      <p className="text-sm text-gray-500">Terms Signed Status</p>
                      {employeeData.terms_signed_at ? (
                        <p className="font-medium text-green-700">
                          Signed: {formatDateTime(employeeData.terms_signed_at)} (Version: {employeeData.terms_version_signed || 'N/A'})
                        </p>
                      ) : (
                        <p className="font-medium text-red-600">
                          Not Signed
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Notes */}
              {employeeData.notes && (
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-gray-500 mb-2 border-b pb-1">NOTES</h4>
                  <p className="text-sm whitespace-pre-wrap">{employeeData.notes}</p>
                </div>
              )}
            </>
          ) : (
             !loading && !error && <div className="text-center text-gray-500">No employee data found.</div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="p-3 border-t flex justify-between sticky bottom-0 bg-gray-50 z-10">
          {/* Resend Invite Button - only show if employee has an email */}
          <div>
            {employeeData && employeeData.email && (
              <Button
                variant="primary"
                onClick={async () => {
                  try {
                    const token = localStorage.getItem('token');
                    if (!token) {
                      throw new Error('Authentication token not found.');
                    }
                    const config = { headers: { 'x-auth-token': token } };
                    
                    // Call the resend invite API endpoint
                    const res = await axios.post(
                      `${API_BASE_URL}/auth/resend-invite`,
                      { userId: employeeData.id }, 
                      config
                    );
                    
                    alert(`Invitation email sent to ${employeeData.email}`);
                  } catch (err) {
                    console.error('Error resending invite:', err);
                    alert(`Failed to resend invitation: ${err.message}`);
                  }
                }}
                disabled={!employeeData || loading}
              >
                Resend Invite Link
              </Button>
            )}
          </div>
          
          {/* Edit and Close buttons */}
          <div className="flex space-x-2">
            <Button
              variant="secondary"
              onClick={() => {
                if (employeeData && onEdit) {
                  onEdit(employeeData); // Pass the fetched data to the edit handler
                }
                onClose(); // Close view modal when opening edit modal
              }}
              disabled={!employeeData || loading}
            >
              Edit
            </Button>
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewEmployeeModal;